import React, { useState, useEffect } from 'react';
import { Info, X, ChevronDown, ChevronUp } from 'lucide-react';

const STORAGE_KEY = 'planner_dismissed_guides';
// Security audit (Feb 2026 · Batch 1): this key holds a plain
// `{guideId: boolean}` object documenting which inline help cards
// the user has dismissed. No PII, no auth state. `getDismissed`
// already shape-checks the payload (rejects non-objects/arrays,
// drops non-boolean values, caps key length) so XSS can't escape
// into render via this read.

function getDismissed() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    // Reject malformed/XSS-injected payloads — only keep a plain object
    // with boolean values (the only shape this component ever writes).
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const clean = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof k === 'string' && k.length < 80 && typeof v === 'boolean') {
        clean[k] = v;
      }
    }
    return clean;
  } catch {
    return {};
  }
}

function setDismissed(id, val) {
  const d = getDismissed();
  d[id] = Boolean(val);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  } catch { /* quota or private-browsing — safe to ignore */ }
}

export default function SectionGuide({ id, title, children, accent = '#14B8A6', icon: Icon = Info, resetKey }) {
  // UX pass (Feb 2026): default to collapsed so the explainer never
  // dominates the page. First-visit users still see the title chip and
  // can click to expand once; dismiss persists in localStorage as before.
  const [visible, setVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const d = getDismissed();
    if (d[id]) setVisible(false);
    else {
      setVisible(true);
      setExpanded(false);
    }
  }, [id, resetKey]);

  if (!visible) return null;

  return (
    <div className="mb-8" data-testid={`guide-${id}`}>
      <div
        className="flex items-center justify-between cursor-pointer select-none group"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-1.5">
          <Icon className="w-4 h-4 text-slate-400" />
          <span className="text-base font-bold text-[#0F2040] group-hover:text-[#0F2040]/80 transition-colors">{title}</span>
          {expanded
            ? <ChevronUp className="w-3 h-3 text-slate-300" />
            : <ChevronDown className="w-3 h-3 text-slate-300" />}
        </div>
        <button
          onClick={e => { e.stopPropagation(); setDismissed(id, true); setVisible(false); }}
          className="text-slate-200 hover:text-slate-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
          title="Dismiss this guide"
          data-testid={`guide-dismiss-${id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      {expanded && (
        <div className="mt-1.5 text-[13px] leading-[1.7] text-slate-500 space-y-1.5">
          {children}
        </div>
      )}
    </div>
  );
}

export function resetGuides() {
  localStorage.removeItem(STORAGE_KEY);
}
